import { Check, FilePlus2, ListMinus, ListPlus, LucideIcon, MessageSquare, Pencil, RotateCw, Send, X } from "lucide-react";

const ACTIONS: Record<string, { label: string; icon: LucideIcon; className: string }> = {
  CREATED: { label: "Budget created", icon: FilePlus2, className: "bg-blue-100 text-blue-700" },
  UPDATED: { label: "Budget updated", icon: Pencil, className: "bg-gray-100 text-gray-600" },
  SUBMITTED: { label: "Submitted for approval", icon: Send, className: "bg-yellow-100 text-yellow-700" },
  APPROVED: { label: "Approved", icon: Check, className: "bg-green-100 text-green-700" },
  REJECTED: { label: "Rejected", icon: X, className: "bg-red-100 text-red-600" },
  REVISED: { label: "Revised and archived", icon: RotateCw, className: "bg-purple-100 text-purple-700" },
  LINE_ITEM_ADDED: { label: "Line item added", icon: ListPlus, className: "bg-blue-100 text-blue-700" },
  LINE_ITEM_UPDATED: { label: "Line item changed", icon: Pencil, className: "bg-gray-100 text-gray-600" },
  LINE_ITEM_DELETED: { label: "Line item deleted", icon: ListMinus, className: "bg-red-100 text-red-600" },
};
const COMMENT = { label: "Comment", icon: MessageSquare, className: "bg-gray-100 text-gray-600" };

const SUBMITTED_PREFIX = "Submitted for approval:";

/**
 * Display entries from the API's rows, keeping its newest-first order.
 * - Rows written before the action column existed have no action: the system's "Submitted for approval:"
 *   prefix identifies submissions, anything else stays a plain comment.
 * - The note is stored on every budget-level row, so it is shown only where it adds something: an edit lists
 *   it under changes, and revisions and repeat saves carry the same text as the entry before.
 * - A save that changed nothing is left out.
 */
export const toLogEntries = (rows: any[]) => {
  const entries: any[] = [];
  let previousNote: string | undefined;
  [...rows].reverse().forEach((row) => {
    const note: string | null = row?.comment ?? null;
    const changes: any[] = row?.changes ?? [];
    const isSubmission = !!note?.startsWith(SUBMITTED_PREFIX);
    const action = row?.action ?? (isSubmission ? "SUBMITTED" : null);
    const carriesNote = ["UPDATED", "REVISED"].includes(row?.action) || !!row?.subject;
    const repeated = carriesNote && note === previousNote;
    if (note !== null) previousNote = note;

    const text = repeated || changes.length ? "" : (isSubmission ? note!.slice(SUBMITTED_PREFIX.length).trim() : note ?? "");
    if (row?.action === "UPDATED" && !changes.length && !text) return;
    entries.unshift({ ...row, changes, text, meta: (action && ACTIONS[action]) || COMMENT });
  });
  return entries;
};

/** "old → new", or just the side that exists when a value was added or removed. */
export const changeText = (change: any) => {
  if (change?.oldValue == null) return `${change?.newValue ?? ""} (added)`;
  if (change?.newValue == null) return `${change.oldValue} (removed)`;
  return `${change.oldValue} → ${change.newValue}`;
};
